import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScanLine, Keyboard, Camera } from 'lucide-react';
import NameplateScanDialog from './NameplateScanDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BarcodeScanner = ({ open, onClose, onAssetFound, onNotFound, categories = [], onModelSaved }) => {
  const { t } = useTranslation();
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [mode, setMode] = useState('camera'); // camera or manual
  const [manualCode, setManualCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const [looking, setLooking] = useState(false);
  const [plateOpen, setPlateOpen] = useState(false);

  const lookupAsset = async (code) => {
    setLooking(true);
    // Our own QR stickers encode a public URL, not the inventory number —
    // resolve those back to the record before falling back to a lookup.
    const asUrl = /\/p\/[apmc]\//.test(code);
    if (asUrl) {
      try {
        const res = await axios.get(`${API}/qr/resolve`, { params: { data: code } });
        if (res.data.kind === 'asset') {
          const a = await axios.get(`${API}/inventory/assets/${res.data.id}`);
          toast.success(t('inv.scanner.found', { name: a.data.name }));
          onAssetFound(a.data);
        } else {
          toast.info(t('inv.scanner.wrongKind', { kind: res.data.kind }));
        }
      } catch {
        toast.error(t('inv.scanner.notRecognised'));
      }
      await stopScanner();
      setLooking(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/inventory/assets/lookup/${encodeURIComponent(code.toUpperCase())}`);
      toast.success(t('inv.scanner.found', { name: r.data.name }));
      onAssetFound(r.data);
      await stopScanner();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(t('inv.scanner.noAsset', { code }));
        onNotFound(code.toUpperCase());
        await stopScanner();
      } else {
        toast.error(t('inv.scanner.lookupFailed'));
      }
    }
    setLooking(false);
  };

  const stopScanner = async () => {
    if (html5QrRef.current) {
      try {
        // Only try to stop if scanner is running
        const scanner = html5QrRef.current;
        html5QrRef.current = null; // Clear ref first to prevent double-stop
        await scanner.stop().catch(() => {});
        await scanner.clear().catch(() => {});
      } catch (err) {
        // Silently ignore stop errors - scanner may not be running
      }
    }
    setScanning(false);
  };

  const startScanner = async () => {
    if (!scannerRef.current || html5QrRef.current) return;
    try {
      const scanner = new Html5Qrcode(scannerRef.current.id);
      html5QrRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 }, aspectRatio: 2.0 },
        (decodedText) => {
          lookupAsset(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error('Camera error:', err);
      toast.error(t('inv.scanner.noCamera'));
      setMode('manual');
      setScanning(false);
    }
  };

  useEffect(() => {
    if (open && mode === 'camera') {
      const timer = setTimeout(startScanner, 300);
      return () => { 
        clearTimeout(timer); 
        // Use async cleanup wrapped in void to avoid unhandled promise
        (async () => { await stopScanner(); })().catch(() => {});
      };
    }
    return () => { 
      // Use async cleanup wrapped in void to avoid unhandled promise
      (async () => { await stopScanner(); })().catch(() => {}); 
    };
  }, [open, mode]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) lookupAsset(manualCode.trim());
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="w-5 h-5" /> {t('inv.scanner.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-3">
          <Button variant={mode === 'camera' ? 'default' : 'outline'} size="sm" onClick={() => setMode('camera')} data-testid="scanner-camera-mode">
            <ScanLine className="w-4 h-4 mr-1" /> {t('inv.scanner.camera')}
          </Button>
          <Button variant={mode === 'manual' ? 'default' : 'outline'} size="sm" onClick={() => { stopScanner(); setMode('manual'); }} data-testid="scanner-manual-mode">
            <Keyboard className="w-4 h-4 mr-1" /> {t('inv.scanner.manual')}
          </Button>
          {/* A tool with no sticker on it yet still has a rating plate. */}
          <Button variant="outline" size="sm" onClick={async () => { await stopScanner(); setPlateOpen(true); }} data-testid="scanner-nameplate-mode">
            <Camera className="w-4 h-4 mr-1" /> {t('inv.scanner.nameplate')}
          </Button>
        </div>

        {mode === 'camera' ? (
          <div className="space-y-3">
            <div
              id="barcode-reader"
              ref={scannerRef}
              className="w-full min-h-[250px] bg-black rounded-lg overflow-hidden"
              data-testid="barcode-camera-view"
            />
            {scanning && (
              <p className="text-xs text-center text-muted-foreground">
                {t('inv.scanner.pointCamera')}
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleManualSubmit} className="space-y-3">
            <div>
              <Label className="text-xs">{t('inv.assets.inventoryNumber')}</Label>
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder={t('auto.inv.ef00001')}
                className="font-mono"
                autoFocus
                data-testid="barcode-manual-input"
              />
            </div>
            <Button type="submit" disabled={looking || !manualCode.trim()} className="w-full" data-testid="barcode-lookup-btn">
              {looking ? t('inv.scanner.lookingUp') : t('inv.scanner.lookup')}
            </Button>
          </form>
        )}
      </DialogContent>

      <NameplateScanDialog open={plateOpen} onOpenChange={setPlateOpen} categories={categories}
        onSaved={(model) => { onModelSaved?.(model); onClose(); }} />
    </Dialog>
  );
};

export default BarcodeScanner;
